/**
 * aiResumeEngine.js
 * 
 * Advanced AI Resume Intelligence Engine
 * - Personalized summary generation (never repeats)
 * - Job description analysis
 * - Bullet enhancement with STAR format
 * - Dynamic personalization
 * - ATS optimization
 * - QA specialization support
 */

// ══════════════════════════════════════════════════════════════════════════════
// QA SPECIALIZATION PROFILES
// ══════════════════════════════════════════════════════════════════════════════

export const QA_SPECIALIZATIONS = {
  manual: {
    id: 'manual',
    label: 'Manual QA Engineer',
    icon: '📋',
    keywords: ['test cases', 'test plans', 'defect tracking', 'regression testing', 'exploratory testing', 'UAT', 'functional testing', 'smoke testing', 'sanity testing', 'acceptance testing', 'boundary value analysis', 'equivalence partitioning'],
    tools: ['JIRA', 'TestRail', 'Zephyr', 'qTest', 'Azure DevOps', 'Linear', 'Notion', 'Confluence', 'PractiTest', 'Xray'],
    metrics: ['defects found', 'test coverage', 'test execution rate', 'defect detection rate'],
    // 2026 smart suggestion data
    suggestedSkills: {
      testing:     ['Manual Testing', 'Functional Testing', 'Regression Testing', 'Exploratory Testing', 'Usability Testing', 'Accessibility Testing', 'UAT'],
      programming: ['SQL', 'Python', 'JavaScript'],
      aiTools:     ['GitHub Copilot', 'ChatGPT', 'AI Test Generation'],
    },
    suggestedTools: {
      testManagement: ['JIRA', 'TestRail', 'Zephyr', 'qTest', 'Azure Test Plans'],
      automation:     ['GitHub Actions', 'Azure DevOps'],
    },
    certifications: ['ISTQB Foundation', 'ISTQB Advanced Test Analyst', 'Certified Agile Tester', 'CSTE'],
    summaryKeywords: ['test strategy', 'defect lifecycle', 'exploratory testing', 'UAT coordination', 'quality gates', 'shift-left testing', 'risk-based testing'],
    bulletStarters: [
      'Designed and executed 500+ test cases covering functional, regression, and UAT scenarios',
      'Reduced defect leakage to production by 45% through structured exploratory testing sessions',
      'Led UAT coordination with 3 business stakeholders, achieving sign-off 2 weeks ahead of schedule',
      'Maintained 95%+ test coverage across 12 sprint releases using TestRail and Zephyr',
    ],
  },
  automation: {
    id: 'automation',
    label: 'Automation QA Engineer',
    icon: '🤖',
    keywords: ['test automation', 'framework development', 'CI/CD integration', 'automated testing', 'test scripts', 'regression automation', 'continuous testing', 'E2E testing', 'component testing', 'parallel execution', 'flakiness reduction', 'Page Object Model', 'BDD', 'TDD'],
    tools: ['Playwright', 'Cypress', 'Selenium', 'WebdriverIO', 'TestNG', 'Jest', 'Vitest', 'Jenkins', 'GitHub Actions', 'GitLab CI', 'Allure', 'k6', 'Cucumber', 'JUnit', 'RestAssured', 'Mocha', 'Chai'],
    metrics: ['automation coverage', 'execution time reduction', 'test stability', 'framework adoption', 'flakiness reduction'],
    suggestedSkills: {
      testing:     ['Regression Testing', 'E2E Testing', 'API Testing', 'Component Testing', 'Visual Testing'],
      automation:  ['Playwright', 'Cypress', 'Selenium WebDriver', 'WebdriverIO', 'Puppeteer', 'Cucumber'],
      programming: ['TypeScript', 'JavaScript', 'Python', 'Java'],
      aiTools:     ['GitHub Copilot', 'Cursor', 'AI Test Generation', 'Testim', 'Mabl'],
    },
    suggestedTools: {
      testManagement: ['JIRA', 'TestRail', 'Zephyr'],
      automation:     ['Jenkins', 'GitHub Actions', 'GitLab CI', 'CircleCI'],
      cicd:           ['Docker', 'Kubernetes'],
      monitoring:     ['Grafana', 'Datadog'],
    },
    certifications: ['ISTQB Advanced Test Automation Engineer', 'Playwright Certified', 'AWS DevOps Professional', 'Certified Agile Tester'],
    summaryKeywords: ['test automation framework', 'CI/CD pipeline', 'E2E coverage', 'parallel test execution', 'flakiness reduction', 'shift-left automation', 'regression suite', 'Page Object Model'],
    bulletStarters: [
      'Built a Playwright + TypeScript E2E framework from scratch covering 400+ user journeys across web and API layers',
      'Reduced regression execution time by 65% by parallelising Cypress tests across 8 workers in GitHub Actions',
      'Achieved 88% automation coverage within 3 sprints, eliminating manual regression entirely',
      'Integrated Allure reporting into Jenkins pipeline, giving the team real-time test visibility',
    ],
  },
  sdet: {
    id: 'sdet',
    label: 'SDET',
    icon: '💻',
    keywords: ['software development', 'test infrastructure', 'API testing', 'microservices testing', 'test frameworks', 'code quality', 'TDD', 'BDD', 'contract testing', 'shift-left', 'developer testing', 'test architecture', 'Cucumber'],
    tools: ['TypeScript', 'Python', 'Java', 'REST Assured', 'Postman', 'Pact', 'Docker', 'Kubernetes', 'Git', 'GraphQL', 'gRPC', 'Kafka', 'Cucumber', 'JUnit', 'TestNG', 'PyTest'],
    metrics: ['code coverage', 'build stability', 'deployment frequency', 'test execution speed'],
    suggestedSkills: {
      testing:     ['API Testing', 'Integration Testing', 'Contract Testing', 'Performance Testing', 'Security Testing'],
      automation:  ['Playwright', 'Cypress', 'Selenium WebDriver', 'Cucumber'],
      programming: ['TypeScript', 'Python', 'Java', 'Go', 'Bash/Shell'],
      databases:   ['PostgreSQL', 'MongoDB', 'Redis'],
      aiTools:     ['GitHub Copilot', 'Cursor', 'Windsurf', 'AI Test Generation'],
    },
    suggestedTools: {
      testManagement: ['JIRA', 'Azure Test Plans'],
      automation:     ['Jenkins', 'GitHub Actions', 'GitLab CI', 'Azure DevOps'],
      cicd:           ['Docker', 'Kubernetes', 'Terraform', 'AWS'],
      monitoring:     ['Grafana', 'Prometheus', 'Datadog', 'ELK Stack'],
    },
    certifications: ['AWS Certified Developer', 'CKAD (Kubernetes)', 'ISTQB Advanced Technical Test Analyst', 'Google Cloud Professional'],
    summaryKeywords: ['test infrastructure', 'TDD', 'BDD', 'contract testing', 'microservices quality', 'developer productivity', 'observability', 'shift-left engineering'],
    bulletStarters: [
      'Architected a contract testing layer using Pact, preventing 23 breaking API changes from reaching production',
      'Built shared test utilities library adopted by 6 engineering squads, reducing test setup time by 70%',
      'Improved build pipeline stability from 74% to 97% by eliminating flaky tests and adding retry logic',
      'Implemented BDD with Cucumber + TypeScript, aligning QA and product on acceptance criteria before dev started',
    ],
  },
  performance: {
    id: 'performance',
    label: 'Performance QA Engineer',
    icon: '⚡',
    keywords: ['load testing', 'stress testing', 'performance optimization', 'scalability testing', 'bottleneck analysis', 'performance metrics', 'capacity planning', 'soak testing', 'spike testing', 'throughput', 'latency', 'p95 response time'],
    tools: ['JMeter', 'k6', 'Gatling', 'LoadRunner', 'New Relic', 'Grafana', 'Prometheus', 'Locust', 'Artillery', 'Datadog APM'],
    metrics: ['response time improvement', 'throughput increase', 'resource utilization', 'concurrent users'],
    suggestedSkills: {
      testing:     ['Performance Testing', 'API Testing', 'Regression Testing'],
      programming: ['Python', 'JavaScript', 'Bash/Shell', 'SQL'],
      cloud:       ['AWS', 'Azure', 'GCP', 'Grafana'],
      aiTools:     ['GitHub Copilot', 'ChatGPT'],
    },
    suggestedTools: {
      testManagement: ['JIRA'],
      automation:     ['Jenkins', 'GitHub Actions', 'GitLab CI'],
      cicd:           ['Docker', 'Kubernetes', 'AWS'],
      monitoring:     ['Grafana', 'Prometheus', 'New Relic', 'Datadog', 'Splunk'],
    },
    certifications: ['ISTQB Performance Testing', 'AWS Solutions Architect', 'Certified k6 Engineer', 'Google Cloud Professional'],
    summaryKeywords: ['load testing', 'p95 latency', 'throughput optimisation', 'capacity planning', 'performance benchmarking', 'APM', 'SLA compliance', 'scalability validation'],
    bulletStarters: [
      'Designed k6 load test scenarios simulating 50K concurrent users, uncovering 3 critical DB bottlenecks pre-launch',
      'Reduced API p95 response time from 2.4s to 380ms through targeted query optimisation and caching strategy',
      'Built Grafana + Prometheus performance dashboard giving engineering real-time SLA visibility',
      'Conducted soak testing over 72-hour windows, identifying memory leaks that would have caused production outages',
    ],
  },
  security: {
    id: 'security',
    label: 'Security QA Engineer',
    icon: '🔒',
    keywords: ['security testing', 'penetration testing', 'vulnerability assessment', 'OWASP Top 10', 'security compliance', 'threat modeling', 'security automation', 'DAST', 'SAST', 'supply chain security', 'zero trust', 'CVE analysis'],
    tools: ['Burp Suite', 'OWASP ZAP', 'Nessus', 'Snyk', 'Wireshark', 'Metasploit', 'Trivy', 'SonarQube', 'Checkmarx', 'Semgrep'],
    metrics: ['vulnerabilities identified', 'security score improvement', 'compliance rate', 'risk mitigation'],
    suggestedSkills: {
      testing:     ['Security Testing', 'API Testing', 'Performance Testing'],
      programming: ['Python', 'Bash/Shell', 'JavaScript', 'SQL'],
      cloud:       ['AWS', 'Azure', 'GCP'],
      aiTools:     ['GitHub Copilot', 'ChatGPT'],
    },
    suggestedTools: {
      testManagement: ['JIRA'],
      automation:     ['Jenkins', 'GitHub Actions', 'GitLab CI'],
      cicd:           ['Docker', 'Kubernetes', 'Terraform'],
      monitoring:     ['Splunk', 'ELK Stack', 'Datadog'],
    },
    certifications: ['OSCP', 'CEH (Certified Ethical Hacker)', 'ISTQB Security Tester', 'CompTIA Security+', 'AWS Security Specialty'],
    summaryKeywords: ['OWASP Top 10', 'penetration testing', 'DAST/SAST', 'vulnerability management', 'threat modeling', 'zero trust', 'security automation', 'compliance'],
    bulletStarters: [
      'Identified 47 OWASP Top 10 vulnerabilities using Burp Suite and OWASP ZAP, all remediated before go-live',
      'Integrated Snyk into CI/CD pipeline, blocking 120+ vulnerable dependencies from reaching production',
      'Conducted threat modeling sessions with engineering leads, reducing attack surface by 38%',
      'Achieved SOC 2 Type II compliance by designing and executing a comprehensive security test plan',
    ],
  },
  aiqa: {
    id: 'aiqa',
    label: 'AI QA Engineer',
    icon: '🧠',
    keywords: ['AI testing', 'LLM testing', 'ML model validation', 'prompt engineering', 'RAG validation', 'hallucination detection', 'bias detection', 'AI ethics', 'model performance', 'data quality', 'MCP testing', 'agentic AI testing', 'evaluation frameworks'],
    tools: ['Python', 'LangChain', 'LlamaIndex', 'MLflow', 'Great Expectations', 'Weights & Biases', 'Pytest', 'Deepeval', 'Ragas', 'Promptfoo'],
    metrics: ['model accuracy', 'hallucination rate', 'data quality score', 'bias reduction', 'inference latency', 'eval score'],
    suggestedSkills: {
      testing:     ['API Testing', 'Functional Testing', 'Performance Testing', 'Security Testing'],
      programming: ['Python', 'TypeScript', 'SQL'],
      aiTools:     ['Prompt Engineering', 'LLM Testing', 'AI Test Generation', 'GitHub Copilot', 'Cursor'],
      cloud:       ['AWS', 'GCP', 'Azure'],
    },
    suggestedTools: {
      testManagement: ['JIRA', 'Linear'],
      automation:     ['GitHub Actions', 'GitLab CI'],
      cicd:           ['Docker', 'Kubernetes', 'AWS'],
      monitoring:     ['Grafana', 'Weights & Biases', 'Datadog'],
    },
    certifications: ['AWS Certified ML Specialty', 'Google Professional ML Engineer', 'DeepLearning.AI LLMOps', 'ISTQB AI Testing'],
    summaryKeywords: ['LLM evaluation', 'RAG pipeline testing', 'prompt engineering', 'hallucination detection', 'MCP validation', 'agentic AI testing', 'model observability', 'AI safety'],
    bulletStarters: [
      'Built an LLM evaluation harness using Deepeval + Ragas, measuring hallucination rate, faithfulness, and answer relevancy across 1,000+ prompts',
      'Designed RAG pipeline test suite validating retrieval accuracy, context relevance, and response grounding for a production chatbot',
      'Implemented prompt regression testing with Promptfoo, catching 15 quality regressions before model updates shipped',
      'Created bias detection framework for a hiring AI model, reducing demographic disparity scores by 42%',
    ],
  },
  mobile: {
    id: 'mobile',
    label: 'Mobile QA Engineer',
    icon: '📱',
    keywords: ['mobile testing', 'iOS testing', 'Android testing', 'device compatibility', 'mobile automation', 'app performance', 'mobile UX', 'gesture testing', 'offline testing', 'push notification testing'],
    tools: ['Appium', 'XCUITest', 'Espresso', 'BrowserStack', 'Sauce Labs', 'Firebase Test Lab', 'Detox', 'XCUI', 'Android Studio', 'Xcode'],
    metrics: ['device coverage', 'crash rate reduction', 'app performance', 'user satisfaction'],
    suggestedSkills: {
      testing:     ['Manual Testing', 'Functional Testing', 'Regression Testing', 'Usability Testing', 'Accessibility Testing'],
      automation:  ['Appium', 'Selenium WebDriver'],
      programming: ['Java', 'Swift', 'Kotlin', 'JavaScript', 'Python'],
      cloud:       ['BrowserStack', 'Sauce Labs', 'AWS Device Farm', 'Firebase Test Lab'],
    },
    suggestedTools: {
      testManagement: ['JIRA', 'TestRail'],
      automation:     ['Jenkins', 'GitHub Actions', 'Bitrise'],
      cicd:           ['Docker', 'AWS'],
      monitoring:     ['Firebase', 'Datadog', 'New Relic'],
    },
    certifications: ['ISTQB Mobile Testing', 'AWS Mobile Developer', 'Google Associate Android Developer'],
    summaryKeywords: ['iOS/Android testing', 'device farm', 'mobile automation', 'Appium framework', 'crash rate reduction', 'cross-platform testing', 'gesture automation'],
    bulletStarters: [
      'Built Appium + WebdriverIO mobile automation suite covering 200+ scenarios across iOS and Android on BrowserStack',
      'Reduced app crash rate from 2.3% to 0.1% by implementing structured device compatibility testing across 50+ device/OS combinations',
      'Automated push notification and deep-link testing, saving 8 hours of manual regression per release',
      'Established mobile performance benchmarks using Firebase Performance Monitoring, improving app launch time by 35%',
    ],
  },
  api: {
    id: 'api',
    label: 'API QA Engineer',
    icon: '🔌',
    keywords: ['API testing', 'REST API', 'GraphQL', 'microservices', 'contract testing', 'API automation', 'integration testing', 'schema validation', 'gRPC', 'WebSocket testing', 'OAuth testing'],
    tools: ['Postman', 'REST Assured', 'SoapUI', 'Pact', 'Karate', 'Newman', 'Insomnia', 'Hoppscotch', 'k6', 'Dredd'],
    metrics: ['API coverage', 'response time', 'error rate reduction', 'contract compliance'],
    suggestedSkills: {
      testing:     ['API Testing', 'Integration Testing', 'Performance Testing', 'Security Testing', 'Regression Testing'],
      programming: ['JavaScript', 'Python', 'Java', 'TypeScript', 'SQL'],
      cloud:       ['AWS', 'Azure', 'GCP'],
      aiTools:     ['GitHub Copilot', 'ChatGPT', 'AI Test Generation'],
    },
    suggestedTools: {
      testManagement: ['JIRA', 'TestRail'],
      automation:     ['Jenkins', 'GitHub Actions', 'GitLab CI'],
      cicd:           ['Docker', 'Kubernetes', 'AWS'],
      monitoring:     ['Grafana', 'Datadog', 'New Relic'],
    },
    certifications: ['Postman API Fundamentals', 'ISTQB Advanced Technical Test Analyst', 'AWS Developer Associate'],
    summaryKeywords: ['REST/GraphQL testing', 'contract testing', 'API automation', 'schema validation', 'microservices quality', 'integration testing', 'API security'],
    bulletStarters: [
      'Built a Karate API automation suite covering 350+ endpoints with schema validation, auth flows, and error boundary testing',
      'Implemented contract testing with Pact between 8 microservices, preventing 19 breaking changes from reaching staging',
      'Reduced API error rate from 3.2% to 0.4% by designing comprehensive negative and boundary test scenarios',
      'Created Newman + GitHub Actions pipeline running full API regression on every PR, cutting QA cycle by 3 days',
    ],
  },
};

// ══════════════════════════════════════════════════════════════════════════════
// EXPERIENCE LEVEL PROFILES
// ══════════════════════════════════════════════════════════════════════════════

const EXPERIENCE_LEVELS = {
  junior: { min: 0, max: 2, label: 'Junior', tone: 'eager', focus: 'learning' },
  mid: { min: 2, max: 5, label: 'Mid-Level', tone: 'confident', focus: 'execution' },
  senior: { min: 5, max: 10, label: 'Senior', tone: 'authoritative', focus: 'leadership' },
  lead: { min: 10, max: 999, label: 'Lead/Principal', tone: 'strategic', focus: 'vision' },
};

// ══════════════════════════════════════════════════════════════════════════════
// SUMMARY GENERATION TEMPLATES (Non-Repetitive)
// ══════════════════════════════════════════════════════════════════════════════

const SUMMARY_TEMPLATES = {
  // Opening variations (never repeat)
  openings: [
    (spec, exp) => `${spec} with ${exp}+ years driving quality excellence across enterprise systems`,
    (spec, exp) => `${exp}+ year ${spec} specializing in scalable test automation and quality engineering`,
    (spec, exp) => `Quality-focused ${spec} bringing ${exp} years of expertise in modern testing practices`,
    (spec, exp) => `${spec} with proven track record of ${exp}+ years building robust QA frameworks`,
    (spec, exp) => `Experienced ${spec} (${exp}+ years) transforming quality processes at scale`,
    (spec, exp) => `${spec} leveraging ${exp} years of hands-on experience in enterprise QA`,
    (spec, exp) => `${exp}-year ${spec} passionate about delivering zero-defect software`,
    (spec, exp) => `Results-oriented ${spec} with ${exp}+ years optimizing testing efficiency`,
    (spec, exp) => `${spec} with ${exp}+ years embedding quality into every stage of the SDLC`,
    (spec, exp) => `Pragmatic ${spec} (${exp}+ yrs) who ships confidence, not just test reports`,
    (spec, exp) => `${exp}-year ${spec} known for turning flaky pipelines into bulletproof CI/CD gates`,
  ],
  
  // Achievement patterns (vary structure)
  achievements: [
    (metric, impact) => `Achieved ${metric} ${impact}`,
    (metric, impact) => `Delivered ${metric} ${impact}`,
    (metric, impact) => `Drove ${metric} ${impact}`,
    (metric, impact) => `Led initiatives resulting in ${metric} ${impact}`,
    (metric, impact) => `Spearheaded ${metric} ${impact}`,
    (metric, impact) => `Engineered solutions achieving ${metric} ${impact}`,
  ],
  
  // Skill showcases (different framings)
  skills: [
    (tools) => `Expert in ${tools}`,
    (tools) => `Proficient across ${tools}`,
    (tools) => `Deep expertise in ${tools}`,
    (tools) => `Specialized in ${tools}`,
    (tools) => `Advanced skills in ${tools}`,
    (tools) => `Hands-on experience with ${tools}`,
  ],
  
  // Closing statements (vary tone)
  closings: [
    () => `Committed to continuous improvement and quality excellence.`,
    () => `Passionate about building reliable, scalable test solutions.`,
    () => `Focused on delivering measurable quality improvements.`,
    () => `Dedicated to advancing QA practices through innovation.`,
    () => `Driven by data-driven quality metrics and team collaboration.`,
    () => `Seeking to leverage expertise in challenging QA environments.`,
    () => `Obsessed with shift-left testing and developer-friendly quality gates.`,
    () => `Thrives in fast-paced, high-ownership engineering cultures.`,
  ],
};

// ══════════════════════════════════════════════════════════════════════════════
// ADVANCED SUMMARY GENERATOR
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Generate personalized, non-repetitive professional summary
 */
export const generateAISummary = (profile, options = {}) => {
  const {
    yearsExperience = 3,
    specialization = 'automation',
    skills = [],
    certifications = [],
    achievements = [],
    jobDescription = '',
    style = 'balanced', // balanced, technical, leadership, ats
    variation = 0, // 0, 1, 2 for different variations
  } = options;
  
  const spec = QA_SPECIALIZATIONS[specialization] || QA_SPECIALIZATIONS.automation;
  
  // Select templates based on variation (ensures uniqueness)
  const openingIdx = (variation * 3) % SUMMARY_TEMPLATES.openings.length;
  const achievementIdx = (variation * 2) % SUMMARY_TEMPLATES.achievements.length;
  const skillIdx = variation % SUMMARY_TEMPLATES.skills.length;
  const closingIdx = (variation * 2) % SUMMARY_TEMPLATES.closings.length;
  
  // Build summary components
  const opening = SUMMARY_TEMPLATES.openings[openingIdx](spec.label, yearsExperience);
  
  // Extract top skills (prioritize from JD if available)
  const topSkills = extractTopSkills(skills, spec, jobDescription).slice(0, 5);
  const skillsText = SUMMARY_TEMPLATES.skills[skillIdx](topSkills.join(', '));
  
  // Generate achievement statement
  const achievementText = generateAchievementStatement(
    achievements,
    spec,
    SUMMARY_TEMPLATES.achievements[achievementIdx]
  );
  
  // Add certifications if present
  const certText = certifications.length > 0
    ? `Certified in ${certifications.slice(0, 2).join(' and ')}.`
    : '';
  
  // Closing
  const closing = SUMMARY_TEMPLATES.closings[closingIdx]();
  
  // Assemble based on style
  let summary = '';
  
  switch (style) {
    case 'technical':
      summary = `${opening}. ${skillsText} with deep expertise in ${spec.tools.slice(0, 3).join(', ')}. ${achievementText} ${certText} ${closing}`;
      break;
      
    case 'leadership':
      summary = `${opening}. ${achievementText} ${skillsText}, leading cross-functional teams to deliver quality at scale. ${certText} ${closing}`;
      break;
      
    case 'ats':
      summary = `${opening}. ${skillsText}. ${achievementText} Expertise includes ${spec.keywords.slice(0, 5).join(', ')}. ${certText}`;
      break;

    case 'faang':
      summary = `${opening}. ${achievementText} ${skillsText}, with a bias for automation-first, observability-driven quality at scale. ${certText} Comfortable owning quality strategy end-to-end in high-velocity, distributed teams.`;
      break;
      
    default: // balanced
      summary = `${opening}. ${skillsText}. ${achievementText} ${certText} ${closing}`;
  }
  
  // Ensure word count is 60-120
  return truncateToWordLimit(summary, 60, 120);
};

/**
 * Generate 3 unique summary variations
 */
export const generateSummaryVariations = (profile, options = {}) => {
  // Use timestamp-based seed so every call produces different index offsets
  const seed = Date.now() % 100;
  return [
    {
      id: 'variation-1',
      style: options.tone || 'balanced',
      text: generateAISummary(profile, { ...options, variation: seed % SUMMARY_TEMPLATES.openings.length, style: options.tone || 'balanced' }),
      label: 'Version A',
      description: 'Recruiter-optimised, achievement-led',
    },
    {
      id: 'variation-2',
      style: options.tone === 'technical' ? 'balanced' : 'technical',
      text: generateAISummary(profile, { ...options, variation: (seed + 3) % SUMMARY_TEMPLATES.openings.length, style: options.tone === 'technical' ? 'balanced' : 'technical' }),
      label: 'Version B',
      description: 'Tool-stack & framework depth',
    },
    {
      id: 'variation-3',
      style: options.tone === 'faang' ? 'leadership' : (options.tone || 'faang'),
      text: generateAISummary(profile, { ...options, variation: (seed + 7) % SUMMARY_TEMPLATES.openings.length, style: options.tone === 'faang' ? 'leadership' : 'faang' }),
      label: 'Version C',
      description: options.tone === 'faang' ? 'Impact & team leadership' : 'FAANG-style, ownership-driven',
    },
  ];
};

// ══════════════════════════════════════════════════════════════════════════════
// JOB DESCRIPTION ANALYZER
// ══════════════════════════════════════════════════════════════════════════════

// Dictionary of general QA/development keywords to cover all common tools and skills
const GENERAL_KEYWORDS = {
  'cucumber': { term: 'Cucumber', type: 'tool', specId: 'automation' },
  'bdd': { term: 'BDD', type: 'skill', specId: 'automation' },
  'tdd': { term: 'TDD', type: 'skill', specId: 'sdet' },
  'junit': { term: 'JUnit', type: 'tool', specId: 'automation' },
  'testng': { term: 'TestNG', type: 'tool', specId: 'automation' },
  'pytest': { term: 'PyTest', type: 'tool', specId: 'automation' },
  'rest assured': { term: 'RestAssured', type: 'tool', specId: 'api' },
  'restassured': { term: 'RestAssured', type: 'tool', specId: 'api' },
  'soapui': { term: 'SoapUI', type: 'tool', specId: 'api' },
  'postman': { term: 'Postman', type: 'tool', specId: 'api' },
  'karate': { term: 'Karate', type: 'tool', specId: 'api' },
  'git': { term: 'Git', type: 'tool', specId: 'manual' },
  'github': { term: 'GitHub', type: 'tool', specId: 'manual' },
  'gitlab': { term: 'GitLab', type: 'tool', specId: 'manual' },
  'maven': { term: 'Maven', type: 'tool', specId: 'automation' },
  'gradle': { term: 'Gradle', type: 'tool', specId: 'automation' },
  'npm': { term: 'npm', type: 'tool', specId: 'automation' },
  'docker': { term: 'Docker', type: 'tool', specId: 'sdet' },
  'kubernetes': { term: 'Kubernetes', type: 'tool', specId: 'sdet' },
  'aws': { term: 'AWS', type: 'tool', specId: 'sdet' },
  'gcp': { term: 'GCP', type: 'tool', specId: 'sdet' },
  'azure': { term: 'Azure', type: 'tool', specId: 'sdet' },
  'terraform': { term: 'Terraform', type: 'tool', specId: 'sdet' },
  'agile': { term: 'Agile', type: 'skill', specId: 'manual' },
  'scrum': { term: 'Scrum', type: 'skill', specId: 'manual' },
  'kanban': { term: 'Kanban', type: 'skill', specId: 'manual' },
  'jira': { term: 'JIRA', type: 'tool', specId: 'manual' },
  'confluence': { term: 'Confluence', type: 'tool', specId: 'manual' },
  'testrail': { term: 'TestRail', type: 'tool', specId: 'manual' },
  'zephyr': { term: 'Zephyr', type: 'tool', specId: 'manual' },
  'qtest': { term: 'qTest', type: 'tool', specId: 'manual' },
  'xray': { term: 'Xray', type: 'tool', specId: 'manual' },
  'sql': { term: 'SQL', type: 'skill', specId: 'manual' },
  'mysql': { term: 'MySQL', type: 'tool', specId: 'manual' },
  'postgresql': { term: 'PostgreSQL', type: 'tool', specId: 'manual' },
  'mongodb': { term: 'MongoDB', type: 'tool', specId: 'manual' },
  'oracle': { term: 'Oracle', type: 'tool', specId: 'manual' },
  'sql server': { term: 'SQL Server', type: 'tool', specId: 'manual' },
  'redis': { term: 'Redis', type: 'tool', specId: 'manual' },
  'cassandra': { term: 'Cassandra', type: 'tool', specId: 'manual' },
  'dynamodb': { term: 'DynamoDB', type: 'tool', specId: 'manual' },
  'javascript': { term: 'JavaScript', type: 'tool', specId: 'automation' },
  'typescript': { term: 'TypeScript', type: 'tool', specId: 'automation' },
  'python': { term: 'Python', type: 'tool', specId: 'automation' },
  'java': { term: 'Java', type: 'tool', specId: 'automation' },
  'c#': { term: 'C#', type: 'tool', specId: 'automation' },
  'csharp': { term: 'C#', type: 'tool', specId: 'automation' },
  'html': { term: 'HTML', type: 'skill', specId: 'manual' },
  'css': { term: 'CSS', type: 'skill', specId: 'manual' },
  'selenium': { term: 'Selenium', type: 'tool', specId: 'automation' },
  'playwright': { term: 'Playwright', type: 'tool', specId: 'automation' },
  'cypress': { term: 'Cypress', type: 'tool', specId: 'automation' },
  'appium': { term: 'Appium', type: 'tool', specId: 'mobile' },
  'robot framework': { term: 'Robot Framework', type: 'tool', specId: 'automation' },
  'katalon': { term: 'Katalon Studio', type: 'tool', specId: 'automation' },
  'puppeteer': { term: 'Puppeteer', type: 'tool', specId: 'automation' },
  'webdriverio': { term: 'WebdriverIO', type: 'tool', specId: 'automation' },
  'espresso': { term: 'Espresso', type: 'tool', specId: 'mobile' },
  'xcuitest': { term: 'XCUITest', type: 'tool', specId: 'mobile' },
  'detox': { term: 'Detox', type: 'tool', specId: 'mobile' },
  'jmeter': { term: 'JMeter', type: 'tool', specId: 'performance' },
  'k6': { term: 'k6', type: 'tool', specId: 'performance' },
  'gatling': { term: 'Gatling', type: 'tool', specId: 'performance' },
  'locust': { term: 'Locust', type: 'tool', specId: 'performance' },
  'loadrunner': { term: 'LoadRunner', type: 'tool', specId: 'performance' },
  'burp suite': { term: 'Burp Suite', type: 'tool', specId: 'security' },
  'owasp': { term: 'OWASP Top 10', type: 'skill', specId: 'security' },
  'snyk': { term: 'Snyk', type: 'tool', specId: 'security' },
  'sonarqube': { term: 'SonarQube', type: 'tool', specId: 'security' },
  'specflow': { term: 'SpecFlow', type: 'tool', specId: 'automation' },
  'behave': { term: 'Behave', type: 'tool', specId: 'automation' },
  'ci/cd': { term: 'CI/CD Pipelines', type: 'skill', specId: 'automation' },
  'devops': { term: 'DevOps', type: 'skill', specId: 'sdet' },
  'e2e': { term: 'E2E Testing', type: 'skill', specId: 'automation' },
  'end-to-end': { term: 'E2E Testing', type: 'skill', specId: 'automation' },
  'regression': { term: 'Regression Testing', type: 'skill', specId: 'manual' },
  'functional': { term: 'Functional Testing', type: 'skill', specId: 'manual' },
  'exploratory': { term: 'Exploratory Testing', type: 'skill', specId: 'manual' },
  'smoke': { term: 'Smoke Testing', type: 'skill', specId: 'manual' },
  'sanity': { term: 'Sanity Testing', type: 'skill', specId: 'manual' },
  'uat': { term: 'UAT', type: 'skill', specId: 'manual' },
  'api testing': { term: 'API Testing', type: 'skill', specId: 'api' },
  'integration': { term: 'Integration Testing', type: 'skill', specId: 'manual' },
  'performance testing': { term: 'Performance Testing', type: 'skill', specId: 'performance' },
  'security testing': { term: 'Security Testing', type: 'skill', specId: 'security' },
  'compatibility': { term: 'Compatibility Testing', type: 'skill', specId: 'manual' },
  'accessibility': { term: 'Accessibility Testing', type: 'skill', specId: 'manual' },
  'wcag': { term: 'WCAG', type: 'skill', specId: 'manual' },
  'test strategy': { term: 'Test Strategy', type: 'skill', specId: 'manual' },
  'test cases': { term: 'Test Cases', type: 'skill', specId: 'manual' },
  'test plan': { term: 'Test Plans', type: 'skill', specId: 'manual' },
  'defect tracking': { term: 'Defect Tracking', type: 'skill', specId: 'manual' },
  'bug tracking': { term: 'Defect Tracking', type: 'skill', specId: 'manual' },
  'claude': { term: 'Claude', type: 'tool', specId: 'aiqa' },
  'perplexity': { term: 'Perplexity AI', type: 'tool', specId: 'aiqa' },
  'mcp': { term: 'MCP (Model Context Protocol)', type: 'tool', specId: 'aiqa' },
  'model context protocol': { term: 'MCP (Model Context Protocol)', type: 'tool', specId: 'aiqa' },
  'swagger': { term: 'Swagger', type: 'tool', specId: 'api' },
  'openapi': { term: 'OpenAPI', type: 'tool', specId: 'api' },
  'ai-assisted test case generation': { term: 'AI-assisted Test Case Generation', type: 'skill', specId: 'aiqa' },
  'ai-assisted bug analysis': { term: 'AI-assisted Bug Analysis', type: 'skill', specId: 'aiqa' },
  'ai-assisted test automation': { term: 'AI-assisted Test Automation', type: 'skill', specId: 'aiqa' },
  'prompt engineering for qa': { term: 'Prompt Engineering for QA', type: 'skill', specId: 'aiqa' },
  'database testing': { term: 'Database Testing', type: 'skill', specId: 'manual' },
  'cross-browser testing': { term: 'Cross-Browser Testing', type: 'skill', specId: 'manual' },
  'stlc': { term: 'STLC', type: 'skill', specId: 'manual' },
  'defect management': { term: 'Defect Management', type: 'skill', specId: 'manual' },
  'risk-based testing': { term: 'Risk-Based Testing', type: 'skill', specId: 'manual' },
  'chatgpt': { term: 'ChatGPT', type: 'tool', specId: 'aiqa' },
  'gemini': { term: 'Gemini', type: 'tool', specId: 'aiqa' },
  'cursor': { term: 'Cursor AI', type: 'tool', specId: 'aiqa' },
  'windsurf': { term: 'Windsurf', type: 'tool', specId: 'aiqa' },
  'test case design': { term: 'Test Case Design', type: 'skill', specId: 'manual' },
  'healthcare domain': { term: 'Healthcare Domain', type: 'skill', specId: 'manual' },
  'healthcare': { term: 'Healthcare Domain', type: 'skill', specId: 'manual' },
  'etl testing': { term: 'ETL Testing', type: 'skill', specId: 'automation' },
  'edi': { term: 'EDI Transactions', type: 'skill', specId: 'manual' },
  'hipaa': { term: 'HIPAA', type: 'skill', specId: 'manual' },
  'backend testing': { term: 'Backend Testing', type: 'skill', specId: 'automation' },
  'batch job testing': { term: 'Batch Job Testing', type: 'skill', specId: 'automation' },
  'manual testing': { term: 'Manual Testing', type: 'skill', specId: 'manual' },
};

// All known QA tools + keywords in one flat lookup for fast matching
const buildMasterLookup = () => {
  const skills = {}; // term -> { category, type: 'skill'|'tool', specId }
  
  // 1. Load specializations
  Object.values(QA_SPECIALIZATIONS).forEach(spec => {
    spec.keywords.forEach(k => {
      if (!skills[k.toLowerCase()]) skills[k.toLowerCase()] = { term: k, type: 'skill', specId: spec.id };
    });
    spec.tools.forEach(t => {
      if (!skills[t.toLowerCase()]) skills[t.toLowerCase()] = { term: t, type: 'tool', specId: spec.id };
    });
  });
  
  // 2. Merge general keywords
  Object.entries(GENERAL_KEYWORDS).forEach(([key, val]) => {
    if (!skills[key]) {
      skills[key] = val;
    }
  });
  
  return skills;
};

const MASTER_LOOKUP = buildMasterLookup();

// Known certifications to scan for
const CERT_PATTERNS = [
  'ISTQB', 'CSTE', 'CSQA', 'OSCP', 'CEH', 'CompTIA Security+',
  'AWS Certified', 'AWS Solutions Architect', 'AWS Developer',
  'Google Cloud', 'Azure Certified', 'CKAD', 'CKA',
  'Certified Scrum Master', 'CSM', 'SAFe', 'PMP',
  'Postman API', 'Playwright Certified',
];

// Seniority signals
const SENIORITY_SIGNALS = {
  lead:   ['lead', 'principal', 'staff', 'architect', 'head of qa', 'qa manager', 'director'],
  senior: ['senior', 'sr.', 'sr ', '5+ years', '6+ years', '7+ years', '8+ years'],
  mid:    ['mid', '3+ years', '4+ years', 'intermediate'],
  junior: ['junior', 'jr.', 'jr ', 'entry level', 'entry-level', '0-2 years', '1+ year', '2+ years', 'fresher', 'graduate'],
};

// Extract years required from JD text
const extractRequiredYears = (jd) => {
  const patterns = [
    /(\d+)\+?\s*(?:to\s*\d+)?\s*years?\s*(?:of\s*)?(?:experience|exp)/i,
    /minimum\s*(\d+)\s*years?/i,
    /at\s*least\s*(\d+)\s*years?/i,
  ];
  for (const p of patterns) {
    const m = jd.match(p);
    if (m) return parseInt(m[1], 10);
  }
  return null;
};

// Detect seniority from JD
const detectSeniority = (jd) => {
  const lower = jd.toLowerCase();
  for (const [level, signals] of Object.entries(SENIORITY_SIGNALS)) {
    if (signals.some(s => lower.includes(s))) return level;
  }
  return 'mid';
};

// Detect primary QA specialization from JD
const detectSpecialization = (jd) => {
  let maxScore = 0;
  let detected = null;
  Object.values(QA_SPECIALIZATIONS).forEach(spec => {
    const score = [...spec.keywords, ...spec.tools]
      .filter(k => jd.includes(k.toLowerCase())).length;
    if (score > maxScore) { maxScore = score; detected = spec; }
  });
  return detected;
};

// Build profile skill set from formData for matching
const buildProfileSkillSet = (profile) => {
  const set = new Set();
  (profile.skills || []).forEach(s => set.add(s.toLowerCase()));
  (profile.tools  || []).forEach(t => set.add(t.toLowerCase()));
  (profile.certifications || []).forEach(c => set.add(c.toLowerCase()));
  return set;
};

// Keyword stuffing guard — max 2 occurrences of same root before flagging
const checkKeywordStuffing = (terms) => {
  const roots = {};
  terms.forEach(t => {
    const root = t.toLowerCase().slice(0, 6);
    roots[root] = (roots[root] || 0) + 1;
  });
  return Object.values(roots).some(c => c > 2);
};

/**
 * analyzeJobDescription — full intelligent JD analysis
 *
 * Returns:
 *   matchScore       number 0-100
 *   grade            'A+' | 'A' | 'B' | 'C' | 'D'
 *   detectedRole     string
 *   detectedSpecId   string
 *   seniority        'junior'|'mid'|'senior'|'lead'
 *   requiredYears    number | null
 *   matched          string[]  — terms in JD that user already has
 *   missing          string[]  — terms in JD that user is missing
 *   missingSkills    string[]  — subset: skill-type missing
 *   missingTools     string[]  — subset: tool-type missing
 *   certifications   string[]  — certs mentioned in JD
 *   atsKeywords      string[]  — top ATS power keywords from JD
 *   totalKeywords    number
 *   stuffingWarning  boolean
 *   priorityActions  { priority, text }[]
 */
export const analyzeJobDescription = (jdText, currentProfile = {}) => {
  const jd = jdText.toLowerCase();

  // 1. Extract all known terms present in JD
  const jdTerms = Object.entries(MASTER_LOOKUP)
    .filter(([key]) => jd.includes(key))
    .map(([, val]) => val);

  // 2. Build user's current skill set
  const profileSet = buildProfileSkillSet(currentProfile);

  // 3. Split into matched vs missing
  const matched = [];
  const missing = [];
  const missingSkills = [];
  const missingTools  = [];

  jdTerms.forEach(({ term, type }) => {
    if (profileSet.has(term.toLowerCase())) {
      matched.push(term);
    } else {
      missing.push(term);
      if (type === 'skill') missingSkills.push(term);
      else missingTools.push(term);
    }
  });

  // Deduplicate
  const uniqMatched = [...new Set(matched)];
  const uniqMissing = [...new Set(missing)];
  const uniqMissingSkills = [...new Set(missingSkills)];
  const uniqMissingTools  = [...new Set(missingTools)];

  // 4. Certifications in JD
  const certifications = CERT_PATTERNS.filter(c => jd.includes(c.toLowerCase()));

  // 5. Detect role + seniority
  const detectedSpec = detectSpecialization(jd);
  const seniority    = detectSeniority(jd);
  const requiredYears = extractRequiredYears(jdText);

  // 6. ATS power keywords (unique meaningful terms from JD)
  const atsKeywords = [...new Set([
    ...uniqMatched.slice(0, 6),
    ...uniqMissing.slice(0, 6),
  ])].slice(0, 12);

  // 7. Match score — weighted
  const totalJdTerms = jdTerms.length;
  const rawScore = totalJdTerms === 0
    ? 0
    : Math.round((uniqMatched.length / totalJdTerms) * 100);

  // Bonus: cert match
  const certBonus = certifications.length > 0 &&
    certifications.some(c => profileSet.has(c.toLowerCase())) ? 5 : 0;

  const matchScore = Math.min(100, rawScore + certBonus);

  const grade =
    matchScore >= 85 ? 'A+' :
    matchScore >= 70 ? 'A'  :
    matchScore >= 55 ? 'B'  :
    matchScore >= 40 ? 'C'  : 'D';

  // 8. Keyword stuffing guard
  const stuffingWarning = checkKeywordStuffing(uniqMatched);

  // 9. Priority actions — personalised, not generic
  const priorityActions = [];

  if (uniqMissingSkills.length > 0) {
    priorityActions.push({
      priority: 'high',
      text: `Add ${uniqMissingSkills.slice(0, 3).join(', ')} to your Technical Skills — these appear directly in the JD.`,
    });
  }
  if (uniqMissingTools.length > 0) {
    priorityActions.push({
      priority: 'high',
      text: `Add ${uniqMissingTools.slice(0, 3).join(', ')} to your Tools section — recruiters scan for these.`,
    });
  }
  if (certifications.length > 0 && !certifications.some(c => profileSet.has(c.toLowerCase()))) {
    priorityActions.push({
      priority: 'medium',
      text: `JD mentions ${certifications[0]} — adding this certification would strengthen your profile.`,
    });
  }
  if (requiredYears && currentProfile.yearsExperience < requiredYears) {
    priorityActions.push({
      priority: 'medium',
      text: `Role requires ${requiredYears}+ years — emphasise your most impactful projects to compensate.`,
    });
  }
  if (matchScore < 40) {
    priorityActions.push({
      priority: 'high',
      text: 'Low match — consider adding the missing keywords above and tailoring your summary to this role.',
    });
  }
  if (stuffingWarning) {
    priorityActions.push({
      priority: 'low',
      text: 'Keyword density looks high — ensure each skill appears naturally in context, not just listed.',
    });
  }

  const res = {
    matchScore,
    grade,
    detectedRole:   detectedSpec?.label  || 'QA Engineer',
    detectedSpecId: detectedSpec?.id     || null,
    seniority,
    requiredYears,
    matched:        uniqMatched,
    missing:        uniqMissing,
    // Alias — consumers may use either field name
    uniqMissing:    uniqMissing,
    missingSkills:  uniqMissingSkills,
    missingTools:   uniqMissingTools,
    certifications,
    atsKeywords,
    totalKeywords:  uniqMatched.length,
    stuffingWarning,
    priorityActions,
    // legacy compat
    strongKeywords: uniqMatched,
    recommendations: priorityActions,
    // suggestions alias used by ReviewStep
    suggestions:    priorityActions,
    keywordSuggestions: uniqMissingSkills.length > 0 || uniqMissingTools.length > 0
      ? { skills: uniqMissingSkills, tools: uniqMissingTools }
      : null,
    categoryMap: {},
  };
  return cleanAndEnhanceAnalysis(res);
};

// ══════════════════════════════════════════════════════════════════════════════
// BULLET ENHANCER
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Enhance bullet point with STAR format and metrics
 */
export const enhanceBullet = (bullet, options = {}) => {
  const {
    specialization = 'automation',
    addMetrics = true,
    style = 'balanced', // balanced, technical, leadership
    variation = 0,
  } = options;
  
  const spec = QA_SPECIALIZATIONS[specialization] || QA_SPECIALIZATIONS.automation;
  
  // Parse bullet to understand context
  const context = parseBulletContext(bullet, spec);
  
  // Generate enhanced version
  const enhanced = generateEnhancedBullet(context, spec, {
    addMetrics,
    style,
    variation,
  });
  
  return enhanced;
};

/**
 * Generate STAR bullets from fragmented inputs (Fallback)
 */
export const generateSTARBulletFallback = ({ situation, task, action, result, role, specialization = 'automation' }) => {
  const spec = QA_SPECIALIZATIONS[specialization] || QA_SPECIALIZATIONS.automation;
  
  const metric = result ? result : spec.metrics[0];
  const act = action ? action : `Led ${spec.label.toLowerCase()} initiatives`;
  
  return {
    variations: [
      { id: 'metric_heavy', text: `Spearheaded ${act.toLowerCase()}, resulting in ${metric.toLowerCase()} across the ${role || 'QA'} team.` },
      { id: 'action_oriented', text: `Architected a solution for ${situation ? situation.toLowerCase() : 'legacy processes'} by ${act.toLowerCase()}, achieving ${metric.toLowerCase()}.` },
      { id: 'concise', text: `${act} to overcome ${task ? task.toLowerCase() : 'process bottlenecks'}, delivering ${metric.toLowerCase()}.` }
    ]
  };
};

/**
 * Parse bullet context
 */
const parseBulletContext = (bullet, spec) => {
  const lower = bullet.toLowerCase();
  
  return {
    action: extractAction(lower),
    tool: spec.tools.find(t => lower.includes(t.toLowerCase())),
    keyword: spec.keywords.find(k => lower.includes(k.toLowerCase())),
    hasMetric: /\d+%|\d+ (tests|cases|hours|days)/.test(lower),
  };
};

/**
 * Extract action verb
 */
const extractAction = (bullet) => {
  const actions = ['worked', 'created', 'built', 'developed', 'tested', 'implemented', 'designed'];
  return actions.find(a => bullet.includes(a)) || 'worked';
};

/**
 * Generate enhanced bullet
 */
const generateEnhancedBullet = (context, spec, options) => {
  const { addMetrics, style, variation } = options;
  
  // Action verb variations
  const actionVerbs = {
    worked: ['Designed', 'Engineered', 'Developed', 'Built', 'Architected'],
    created: ['Established', 'Implemented', 'Launched', 'Initiated', 'Deployed'],
    tested: ['Validated', 'Verified', 'Assessed', 'Evaluated', 'Analyzed'],
    developed: ['Engineered', 'Built', 'Created', 'Constructed', 'Formulated'],
  };
  
  const verb = actionVerbs[context.action]?.[variation % 5] || 'Developed';
  
  // Generate metric
  const metric = addMetrics ? generateRealisticMetric(spec) : '';
  
  // Build enhanced bullet
  let enhanced = '';
  
  switch (style) {
    case 'technical':
      enhanced = `${verb} ${context.tool || spec.tools[0]}-based ${context.keyword || spec.keywords[0]} ${metric}`;
      break;
      
    case 'leadership':
      enhanced = `${verb} and led ${context.keyword || spec.keywords[0]} initiative ${metric}, collaborating with cross-functional teams`;
      break;
      
    default: // balanced
      enhanced = `${verb} ${context.keyword || spec.keywords[0]} using ${context.tool || spec.tools[0]} ${metric}`;
  }
  
  return enhanced;
};

/**
 * Generate realistic metrics
 */
const generateRealisticMetric = (spec) => {
  const metrics = {
    manual: [
      'reducing defect leakage by 45%',
      'improving test coverage from 65% to 92%',
      'executing 200+ test cases per sprint',
    ],
    automation: [
      'reducing regression execution time by 58%',
      'achieving 85% automation coverage',
      'cutting release cycle time by 40%',
    ],
    sdet: [
      'improving build stability from 78% to 96%',
      'reducing deployment time by 52%',
      'increasing code coverage to 88%',
    ],
    performance: [
      'improving response time by 63%',
      'supporting 10K+ concurrent users',
      'reducing resource utilization by 35%',
    ],
    security: [
      'identifying 47 critical vulnerabilities',
      'achieving 98% security compliance',
      'reducing security incidents by 72%',
    ],
    aiqa: [
      'improving model accuracy from 82% to 94%',
      'reducing bias by 38%',
      'validating 50+ ML models',
    ],
  };
  
  const specMetrics = metrics[spec.id] || metrics.automation;
  return specMetrics[Math.floor(Math.random() * specMetrics.length)];
};

// ══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

export const getExperienceLevel = (years) => {
  for (const [, level] of Object.entries(EXPERIENCE_LEVELS)) {
    if (years >= level.min && years <= level.max) {
      return level;
    }
  }
  return EXPERIENCE_LEVELS.mid;
};

const extractTopSkills = (skills, spec, jd) => {
  if (!skills || skills.length === 0) {
    return spec.tools.slice(0, 5);
  }
  
  // Prioritize skills mentioned in JD
  if (jd) {
    const jdLower = jd.toLowerCase();
    const prioritized = skills.filter(skill => 
      jdLower.includes(skill.toLowerCase())
    );
    
    if (prioritized.length > 0) {
      return prioritized.slice(0, 5);
    }
  }
  
  return skills.slice(0, 5);
};

const generateAchievementStatement = (achievements, spec, template) => {
  if (!achievements || achievements.length === 0) {
    // Generate generic achievement based on specialization
    const metric = spec.metrics[0];
    const impact = 'across enterprise systems';
    return template(metric, impact);
  }
  
  // Use first achievement
  const achievement = achievements[0];
  return template(achievement.metric || spec.metrics[0], achievement.impact || 'for the organization');
};

const truncateToWordLimit = (text, min, max) => {
  const words = text.split(' ');
  
  if (words.length < min) {
    return text; // Too short, return as is
  }
  
  if (words.length <= max) {
    return text; // Perfect length
  }
  
  // Truncate to max words
  return words.slice(0, max).join(' ') + '.';
};

// Classify keyword as skill, tool, or certification (with exact and substring fallback matching)
export const getKeywordType = (keyword) => {
  if (!keyword) return 'skill';
  const lower = keyword.toLowerCase().trim();
  
  // 1. Direct exact match
  if (MASTER_LOOKUP[lower]) return MASTER_LOOKUP[lower].type;
  
  // 2. Substring match fallback (e.g. "Cursor AI" matching "cursor", or "Perplexity AI" matching "perplexity")
  for (const [key, val] of Object.entries(MASTER_LOOKUP)) {
    if (lower === key || lower.includes(key) || key.includes(lower)) {
      return val.type;
    }
  }
  
  return 'skill';
};

// Clean, deduplicate and correct classification of JD analysis results
export const cleanAndEnhanceAnalysis = (analysis) => {
  if (!analysis) return analysis;

  const normalizeList = (list) => {
    if (!list || !Array.isArray(list)) return [];
    
    // Deduplicate case-insensitively, keeping the capitalized/preferred version
    const seen = new Map();
    list.forEach(item => {
      const clean = item.trim();
      const lower = clean.toLowerCase();
      if (!seen.has(lower)) {
        seen.set(lower, clean);
      } else {
        const existing = seen.get(lower);
        // Prefer capitalized or longer term
        if (clean !== existing && clean.toLowerCase() === clean) {
          // clean is lowercase, keep existing
        } else {
          seen.set(lower, clean);
        }
      }
    });

    let uniq = Array.from(seen.values());

    // Substring deduplication:
    // e.g. remove "exploratory" if "exploratory testing" exists.
    // e.g. remove "test plan" if "test plans" or "test plan design" exists.
    // e.g. remove "prompt engineering" if "prompt engineering for qa" exists.
    uniq = uniq.filter((item, idx) => {
      return !uniq.some((other, otherIdx) => {
        if (idx === otherIdx) return false;
        const lowerItem = item.toLowerCase();
        const lowerOther = other.toLowerCase();
        const isSubstring = lowerOther.includes(lowerItem) && lowerItem.length < lowerOther.length;
        if (!isSubstring) return false;
        
        const isRelated = 
          lowerOther.includes('testing') || 
          lowerOther.includes('plan') || 
          lowerOther.includes('engineering') || 
          lowerOther.includes('automation') ||
          lowerOther.includes('case') ||
          lowerOther.includes('management') ||
          lowerOther.includes('analysis');
          
        return isRelated;
      });
    });

    return uniq;
  };

  const matched = normalizeList(analysis.matched || analysis.strongKeywords || []);
  const missing = normalizeList(analysis.missing || analysis.uniqMissing || []);
  const certifications = normalizeList(analysis.certifications || []);
  const atsKeywords = normalizeList(analysis.atsKeywords || []);

  // Classify missing keywords based on our updated getKeywordType
  const missingTools = [];
  const missingSkills = [];

  missing.forEach(kw => {
    const type = getKeywordType(kw);
    if (type === 'tool') {
      missingTools.push(kw);
    } else {
      missingSkills.push(kw);
    }
  });

  const uniqMissing = [...missingTools, ...missingSkills];

  // Re-generate matched details if present
  const matchedTools = [];
  const matchedSkills = [];
  matched.forEach(kw => {
    const type = getKeywordType(kw);
    if (type === 'tool') matchedTools.push(kw);
    else matchedSkills.push(kw);
  });

  // personal priority recommendations
  const priorityActions = [];
  if (missingSkills.length > 0) {
    priorityActions.push({
      priority: 'high',
      text: `Add ${missingSkills.slice(0, 3).join(', ')} to your Technical Skills — these appear directly in the JD.`,
    });
  }
  if (missingTools.length > 0) {
    priorityActions.push({
      priority: 'high',
      text: `Add ${missingTools.slice(0, 3).join(', ')} to your Tools section — recruiters scan for these.`,
    });
  }
  if (certifications.length > 0) {
    priorityActions.push({
      priority: 'medium',
      text: `JD mentions ${certifications[0]} — adding this certification would strengthen your profile.`,
    });
  }

  const matchScore = analysis.matchScore ?? Math.min(100, Math.round((matched.length / (matched.length + uniqMissing.length || 1)) * 100));
  const grade =
    matchScore >= 85 ? 'A+' :
    matchScore >= 70 ? 'A'  :
    matchScore >= 55 ? 'B'  :
    matchScore >= 40 ? 'C'  : 'D';

  return {
    ...analysis,
    matchScore,
    grade,
    matched,
    missing: uniqMissing,
    uniqMissing,
    missingTools,
    missingSkills,
    certifications,
    atsKeywords: atsKeywords.length > 0 ? atsKeywords : [...new Set([...matched.slice(0, 6), ...uniqMissing.slice(0, 6)])].slice(0, 12),
    totalKeywords: matched.length,
    priorityActions: priorityActions.length > 0 ? priorityActions : (analysis.priorityActions || []),
    recommendations: priorityActions.length > 0 ? priorityActions : (analysis.recommendations || []),
    suggestions: priorityActions.length > 0 ? priorityActions : (analysis.suggestions || []),
    keywordSuggestions: uniqMissing.length > 0 ? { skills: missingSkills, tools: missingTools } : null,
  };
};

export default {
  generateAISummary,
  generateSummaryVariations,
  analyzeJobDescription,
  enhanceBullet,
  generateSTARBulletFallback,
  QA_SPECIALIZATIONS,
  getKeywordType,
  cleanAndEnhanceAnalysis,
};
